import * as THREE from 'three';
import { SceneNode } from '../core/SceneNode';
import { Vector3, Quaternion } from '../types';
import { SceneNodeManager } from '../core/SceneNodeManager';

/**
 * SceneNodeTransformTest - Dedicated test class for SceneNode transform hierarchy testing
 * Tests synchronization between SceneNode system and Three.js scene graph
 */
export class SceneNodeTransformTest {
    private testSceneNodes: TestSceneNode[] = [];
    private animationId: number | null = null;
    private isRunning: boolean = false;
    private sceneManager: SceneNodeManager;

    constructor(sceneManager: SceneNodeManager) {
        this.sceneManager = sceneManager;
    }

    /**
     * Toggle the transform test - start if not running, stop if running
     */
    public toggle(): void {
        if (this.isRunning) {
            this.stop();
        } else {
            this.start();
        }
    }

    /**
     * Start the SceneNode transform hierarchy test
     */
    public start(): void {
        if (this.isRunning) {
            console.log('🧪 SceneNode Transform Test is already running');
            return;
        }

        console.log('🧪 Creating SceneNode Transform Test...');
        
        // Clear any existing test nodes
        this.stop();
        
        // Create parent node at origin
        const parentNode = new TestSceneNode('Parent', 'box');
        parentNode.transform.position.value = new Vector3(0, 0, 0);
        parentNode.transform.scale.value = new Vector3(1.2, 1.2, 1.2);
        parentNode.setColor(0xff0000); // Red parent
        
        // Create first child offset to the right
        const child1 = new TestSceneNode('Child1', 'sphere');
        child1.transform.position.value = new Vector3(2, 0, 0);
        child1.transform.scale.value = new Vector3(0.8, 0.8, 0.8);
        child1.setColor(0x00ff00); // Green child
        
        // Create second child offset up from first child
        const child2 = new TestSceneNode('Child2', 'cylinder');
        child2.transform.position.value = new Vector3(0, 1.5, 0);
        child2.transform.scale.value = new Vector3(0.7, 0.7, 0.7);
        child2.setColor(0x0000ff); // Blue child
        
        // Create third child as grandchild
        const grandchild = new TestSceneNode('Grandchild', 'torus');
        grandchild.transform.position.value = new Vector3(0, 0, 1.5);
        grandchild.transform.scale.value = new Vector3(0.5, 0.5, 0.5);
        grandchild.setColor(0xffff00); // Yellow grandchild
        
        // Set up hierarchy
        parentNode.addChild(child1);
        child1.addChild(child2);
        child2.addChild(grandchild);
        
        // Add to scene
        this.sceneManager.addSceneNode(parentNode);
        
        // Store references for cleanup
        this.testSceneNodes = [parentNode, child1, child2, grandchild];
        
        // Start animation
        this.startAnimation();
        this.isRunning = true;
        
        console.log('✅ SceneNode Transform Test created!');
        console.log('📋 Test includes:');
        console.log('   - Parent (red box) rotating around Y axis');
        console.log('   - Child1 (green sphere) orbiting parent in figure-8 pattern');
        console.log('   - Child2 (blue cylinder) rotating locally on Child1');
        console.log('   - Grandchild (yellow torus) scaling rhythmically on Child2');
        console.log('   - Coordinate axes on each object (X=red, Y=green, Z=blue)');
        console.log('');
        console.log('🔍 WHAT TO LOOK FOR:');
        console.log('✅ CORRECT behavior:');
        console.log('   - Smooth rotation/movement/scaling without jittering');
        console.log('   - Child objects move WITH their parents (inheritance)');
        console.log('   - Green sphere should orbit around red box center');
        console.log('   - Blue cylinder should stay attached to green sphere');
        console.log('   - Yellow torus should stay attached to blue cylinder');
        console.log('   - All objects maintain their relative positions');
        console.log('');
        console.log('❌ INCORRECT behavior (bugs):');
        console.log('   - Objects jumping or teleporting');
        console.log('   - Children not following parents properly');
        console.log('   - Jittery or stuttering movement');
        console.log('   - Objects appearing at wrong positions');
        console.log('   - Transform inheritance not working');
        console.log('');
        console.log('⚡ Press t again to clear the test');
    }
    
    /**
     * Stop and clear the SceneNode transform test
     */
    public stop(): void {
        if (this.testSceneNodes.length > 0) {
            console.log('🧹 Clearing SceneNode Transform Test...');
            
            // Stop animation
            this.stopAnimation();
            
            // Remove all test nodes from scene
            this.testSceneNodes.forEach(node => {
                this.sceneManager.removeSceneNode(node);
                node.destroy();
            });
            
            this.testSceneNodes = [];
            this.isRunning = false;
            console.log('✅ SceneNode Transform Test cleared');
        }
    }

    /**
     * Start the animation loop
     */
    private startAnimation(): void {
        if (this.testSceneNodes.length === 0) return;

        const [parentNode, child1, child2, grandchild] = this.testSceneNodes;
        let time = 0;
        let validationCounter = 0;
        
        const animate = () => {
            if (!this.isRunning || this.testSceneNodes.length === 0) {
                return; // Test stopped or cleared
            }
            
            time += 0.016; // Roughly 60fps
            validationCounter++;
            
            // Rotate parent around Y axis (more visible rotation)
            const parentRot = Quaternion.fromAxisAngle(Vector3.up(), time * 1.0);
            parentNode.transform.rotation.value = parentRot;
            
            // Move child1 in a circle relative to parent
            const childOffset = new Vector3(
                Math.cos(time) * 2,
                Math.sin(time * 2) * 0.5,
                Math.sin(time) * 2
            );
            child1.transform.position.value = childOffset;
            
            // Rotate child2 around local X axis (more visible rotation)
            const child2Rot = Quaternion.fromAxisAngle(Vector3.right(), time * 3.0);
            child2.transform.rotation.value = child2Rot;
            
            // Scale grandchild rhythmically
            const scaleVal = 0.4 + Math.sin(time * 3) * 0.2;
            grandchild.transform.scale.value = new Vector3(scaleVal, scaleVal, scaleVal);
            
            // Validate transform synchronization every 60 frames (1 second)
            if (validationCounter % 60 === 0) {
                this.validateTransformSync();
            }
            
            this.animationId = requestAnimationFrame(animate);
        };
        
        animate();
    }
    
    /**
     * Validate that SceneNode transforms match Three.js object transforms
     */
    private validateTransformSync(): void {
        this.testSceneNodes.forEach(node => {
            const sceneNodeMatrix = node.transform.getWorldMatrix();
            const threeMesh = (node as any)._testMesh as THREE.Mesh;
            
            if (!threeMesh) return;
            
            // Update Three.js world matrix
            threeMesh.updateMatrixWorld(true);
            const threeMatrix = threeMesh.matrixWorld;
            
            // Compare matrices (simplified check for major discrepancies)
            const tolerance = 0.1;
            const scenePos = new THREE.Vector3().setFromMatrixPosition(sceneNodeMatrix);
            const threePos = new THREE.Vector3().setFromMatrixPosition(threeMatrix);
            
            const distance = scenePos.distanceTo(threePos);
            
            if (distance > tolerance) {
                console.warn(`🚨 TRANSFORM SYNC ISSUE detected in ${node.name}:`);
                console.warn(`   SceneNode position: ${scenePos.x.toFixed(3)}, ${scenePos.y.toFixed(3)}, ${scenePos.z.toFixed(3)}`);
                console.warn(`   Three.js position:  ${threePos.x.toFixed(3)}, ${threePos.y.toFixed(3)}, ${threePos.z.toFixed(3)}`);
                console.warn(`   Distance difference: ${distance.toFixed(3)} (tolerance: ${tolerance})`);
                console.warn(`   This indicates a bug in transform synchronization!`);
            }
        });
    }

    /**
     * Stop the animation loop
     */
    private stopAnimation(): void {
        if (this.animationId !== null) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    }

    /**
     * Check if the test is currently running
     */
    public get running(): boolean {
        return this.isRunning;
    }

    /**
     * Cleanup method to be called when the test is no longer needed
     */
    public dispose(): void {
        this.stop();
    }
}

/**
 * Simple test SceneNode for transform hierarchy testing
 */
class TestSceneNode extends SceneNode {
    private _testMesh: THREE.Mesh | null = null;
    private _geometry: THREE.BufferGeometry | null = null;
    private _material: THREE.MeshBasicMaterial | null = null;
    
    constructor(name: string, shapeType: 'box' | 'sphere' | 'cylinder' | 'torus' = 'box') {
        super(name);
        this.createTestMesh(shapeType);
    }
    
    private createTestMesh(shapeType: string): void {
        // Create geometry based on shape type
        switch (shapeType) {
            case 'sphere':
                this._geometry = new THREE.SphereGeometry(0.5, 16, 16);
                break;
            case 'cylinder':
                this._geometry = new THREE.CylinderGeometry(0.5, 0.5, 1, 16);
                break;
            case 'torus':
                this._geometry = new THREE.TorusGeometry(0.4, 0.2, 8, 16);
                break;
            case 'box':
            default:
                this._geometry = new THREE.BoxGeometry(1, 1, 1);
                break;
        }
        
        // Create material with wireframe AND solid to make hierarchy clear
        this._material = new THREE.MeshBasicMaterial({ 
            color: 0x888888,
            wireframe: true,
            transparent: true,
            opacity: 0.9
        });
        
        // Create mesh
        this._testMesh = new THREE.Mesh(this._geometry, this._material);
        this._testMesh.userData.sceneNode = this;
        this._mesh = this._testMesh;
        
        // Add visual hierarchy indicators (connection lines)
        this.addHierarchyIndicators();
    }
    
    private addHierarchyIndicators(): void {
        if (!this._testMesh) return;
        
        // Add coordinate axes to show object orientation
        const axesGroup = new THREE.Group();
        
        // X axis (red)
        const xGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(0.7, 0, 0)
        ]);
        const xMaterial = new THREE.LineBasicMaterial({ color: 0xff4444 });
        const xAxis = new THREE.Line(xGeometry, xMaterial);
        axesGroup.add(xAxis);
        
        // Y axis (green)
        const yGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(0, 0.7, 0)
        ]);
        const yMaterial = new THREE.LineBasicMaterial({ color: 0x44ff44 });
        const yAxis = new THREE.Line(yGeometry, yMaterial);
        axesGroup.add(yAxis);
        
        // Z axis (blue)
        const zGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(0, 0, 0.7)
        ]);
        const zMaterial = new THREE.LineBasicMaterial({ color: 0x4444ff });
        const zAxis = new THREE.Line(zGeometry, zMaterial);
        axesGroup.add(zAxis);
        
        this._testMesh.add(axesGroup);
    }
    
    public setColor(color: number): void {
        if (this._material) {
            this._material.color.setHex(color);
        }
    }
    
    protected calculateBoundingBox(): THREE.Box3 {
        if (this._geometry) {
            if (!this._geometry.boundingBox) {
                this._geometry.computeBoundingBox();
            }
            return this._geometry.boundingBox || new THREE.Box3();
        }
        return new THREE.Box3();
    }
    
    protected onUpdate(deltaTime: number): void {
        // Sync transform to Three.js mesh (similar to Mesh3D approach)
        if (this._testMesh && (this.transform.isDirty || this.transform.hasInterpolatingProperties())) {
            const hasThreeParent = this._testMesh.parent && this._testMesh.parent.userData?.sceneNode;
            
            if (hasThreeParent) {
                // Has THREE.js Parent → Use LOCAL transform (THREE.js will handle world matrix)
                const localPos = this.transform.position.value ?? Vector3.zero();
                const localRot = this.transform.rotation.value ?? Quaternion.identity();
                const localScale = this.transform.scale.value ?? Vector3.one();
                
                this._testMesh.position.set(localPos.x, localPos.y, localPos.z);
                this._testMesh.quaternion.set(localRot.x, localRot.y, localRot.z, localRot.w);
                this._testMesh.scale.set(localScale.x, localScale.y, localScale.z);
                this._testMesh.matrixAutoUpdate = true; // Let THREE.js handle world matrix
            } else {
                // No THREE.js Parent → Use WORLD matrix (root object)
                this._testMesh.matrix.copy(this.transform.getWorldMatrix());
                this._testMesh.matrixAutoUpdate = false; // We manage the matrix manually
            }
        }
        
        super.onUpdate(deltaTime);
    }
    
    protected onDestroy(): void {
        if (this._geometry) {
            this._geometry.dispose();
        }
        if (this._material) {
            this._material.dispose();
        }
        super.onDestroy();
    }
} 